import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { MASCOT_ICONS } from "../lib/icons";
import { Lightbulb, Quote, Activity } from "lucide-react";
import { useLang } from "../lib/i18n";

const CAT_COLORS = {
  phrase: "#E07A5F",
  exercise: "#81B29A",
  info: "#F2CC8F",
};
const CAT_ICONS = { phrase: Quote, exercise: Activity, info: Lightbulb };

export default function Tips() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const selected = params.get("emotion") || "all";
  const [mascots, setMascots] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/mascots").then((r) => setMascots(r.data));
  }, [lang]);

  useEffect(() => {
    setLoading(true);
    const q = selected !== "all" ? `?emotion=${selected}` : "";
    api.get(`/tips${q}`).then((r) => setTips(r.data)).finally(() => setLoading(false));
  }, [selected, lang]);

  const setEmotion = (id) => {
    const p = new URLSearchParams(params);
    if (id === "all") p.delete("emotion"); else p.set("emotion", id);
    setParams(p);
  };

  const catLabel = (cat) => t(`tips.cat.${cat}`);

  return (
    <div className="space-y-8" data-testid="tips-page">
      <header>
        <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B]">{t("tips.title")}</h1>
        <p className="text-[#8A8D91] mt-2 max-w-2xl">{t("tips.subtitle")}</p>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="emotion-filters">
        <button
          data-testid="filter-all"
          onClick={() => setEmotion("all")}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
            selected === "all" ? "bg-[#3D405B] text-white border-[#3D405B]" : "bg-white text-[#3D405B] border-[#E8E6E1] hover:border-[#3D405B]"
          }`}
        >
          {t("tips.filter.all")}
        </button>
        {mascots.map((m) => {
          const Icon = MASCOT_ICONS[m.icon];
          const active = selected === m.id;
          return (
            <button
              key={m.id}
              data-testid={`filter-${m.id}`}
              onClick={() => setEmotion(m.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium border inline-flex items-center gap-2 transition-colors ${
                active ? "text-white border-transparent" : "bg-white text-[#3D405B] border-[#E8E6E1] hover:border-[#3D405B]"
              }`}
              style={active ? { background: m.color } : {}}
            >
              {Icon && <Icon className="w-4 h-4" />} {m.emotion}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-[#8A8D91]">{t("tips.loading")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" data-testid="tips-grid">
          {tips.map((tip, idx) => {
            const color = CAT_COLORS[tip.category] || CAT_COLORS.info;
            const CatIcon = CAT_ICONS[tip.category] || Lightbulb;
            const mascot = mascots.find((m) => m.id === tip.emotion);
            return (
              <article
                key={tip.id}
                data-testid={`tip-card-${idx}`}
                className="bg-white border border-[#E8E6E1] rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-shadow flex flex-col"
              >
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: color }}>
                      <CatIcon className="w-3.5 h-3.5" /> {catLabel(tip.category)}
                    </span>
                    {mascot && (
                      <span className="text-xs text-[#8A8D91]">{t("tips.with")} {mascot.name} · {mascot.emotion}</span>
                    )}
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl text-[#3D405B] leading-snug">{tip.title}</h3>
                  <p className="mt-3 text-[#3D405B]/85 leading-relaxed whitespace-pre-line">{tip.content}</p>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
